
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';

export class ImageLibraryClient {
  static supabase = supabase;

  /**
   * Copy an image from image_library to workbook_images for project assignment
   */
  static async copyToWorkbookImages(
    imageLibraryId: string, 
    userId: string, 
    projectId: string
  ): Promise<void> {
    // 1. Fetch image data from image_library
    const { data: imageData, error: fetchError } = await supabase
      .from('image_library')
      .select('*')
      .eq('id', imageLibraryId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch image from library: ${fetchError.message}`);
    }

    if (!imageData) {
      throw new Error('Image not found in library');
    }

    // 2. Extract filename from URL
    const filename = imageData.image_url.split('/').pop() || `image-${Date.now()}.webp`;
    
    // 3. Insert into workbook_images
    const { error: insertError } = await supabase
      .from('workbook_images')
      .insert({
        user_id: userId,
        workbook_id: projectId,
        image_url: imageData.image_url, // Cloudinary URL works perfectly!
        storage_path: imageData.storage_path,
        filename: filename, // Extracted from URL
        alt_text: imageData.description,
        generation_prompt: imageData.original_prompt,
        width: imageData.width,
        height: imageData.height,
        is_generated: true,
        file_size: null // Not available in image_library
      });

    if (insertError) {
      throw new Error(`Failed to copy image to workbook: ${insertError.message}`);
    }
  }

  /**
   * Check if a public image has been copied to a specific project
   */
  static async checkImageInProject(
    imageLibraryId: string, 
    userId: string, 
    projectId: string
  ): Promise<boolean> {
    // Check if there's already a workbook_images entry with the same image_url and workbook_id
    const { data: imageData, error: fetchError } = await supabase
      .from('image_library')
      .select('image_url')
      .eq('id', imageLibraryId)
      .single();

    if (fetchError || !imageData) {
      return false;
    }

    const { data, error } = await supabase
      .from('workbook_images')
      .select('id')
      .eq('user_id', userId)
      .eq('workbook_id', projectId)
      .eq('image_url', imageData.image_url)
      .is('deleted_at', null)
      .single();

    return !error && !!data;
  }
}
